"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.INLINE_SCRIPT_MAX_LENGTH = exports.ADVENTURE_SCRIPT_MAX_PRELUDE_LENGTH = void 0;
exports.getAdventurePrelude = getAdventurePrelude;
exports.isMultiStatementCondition = isMultiStatementCondition;
exports.resolveConditionExecutionKind = resolveConditionExecutionKind;
exports.resolveExpressionExecutionKind = resolveExpressionExecutionKind;
exports.buildScriptFunctionBody = buildScriptFunctionBody;
exports.buildExecutableFunctionSource = buildExecutableFunctionSource;
exports.validateForbiddenPatterns = validateForbiddenPatterns;
exports.validatePrelude = validatePrelude;
exports.validateExecutionScript = validateExecutionScript;
const adventureScriptReservedNames_1 = require("../parser/adventureScriptReservedNames");
/** Merged `## Scripts` prelude cap (sub-adventures + main). Policy limit for parse/compile cost, not a V8 hard limit. */
exports.ADVENTURE_SCRIPT_MAX_PRELUDE_LENGTH = 128000;
exports.INLINE_SCRIPT_MAX_LENGTH = 5000;
const FORBIDDEN_PATTERNS = [
    { pattern: /\brequire\s*\(/, name: 'require()' },
    { pattern: /\bimport\s+/, name: 'import' },
    { pattern: /\bexport\s+/, name: 'export' },
    { pattern: /\beval\s*\(/, name: 'eval()' },
    { pattern: /\bnew\s+Function/, name: 'Function constructor' },
    { pattern: /\b__proto__\b/, name: '__proto__' },
    { pattern: /\bprototype\b/, name: 'prototype' },
    { pattern: /\bconstructor\[/, name: 'constructor access' },
    { pattern: /\bprocess\./, name: 'process object' },
    { pattern: /\bglobal\./, name: 'global object' },
    { pattern: /\bwindow\./, name: 'window object' },
    { pattern: /\bdocument\./, name: 'document object' }
];
function getAdventurePrelude(adventureManager, gameSession) {
    const prelude = adventureManager?.getAdventure(gameSession.adventureId)?.scripts;
    const trimmed = prelude?.trim();
    return trimmed ? trimmed : undefined;
}
/** True when inline condition is multi-statement (not a single expression). */
function isMultiStatementCondition(inline) {
    const t = inline.trim();
    if (!t) {
        return false;
    }
    // IIFE and other parenthesized single expressions (common for activity Requires: scripts)
    if (/^\(\s*function\b/.test(t) || /^\(\s*async\s+function\b/.test(t)) {
        return false;
    }
    if (/^\s*(function|if|for|while|switch|try)\s/.test(t)) {
        return true;
    }
    if (/^\s*(let|var)\s/.test(t)) {
        return true;
    }
    if (/\breturn\b/.test(t) && (t.includes(';') || t.includes('\n'))) {
        return true;
    }
    const withoutTrailingSemi = t.replace(/;\s*$/, '');
    if (withoutTrailingSemi.includes(';')) {
        return true;
    }
    // Newlines alone do not make a multi-statement script. Formatted single
    // expressions (e.g. getFoo(\n  a,\n  { k: v }\n)) must stay expression-kind
    // so executeScriptWithReturnValue wraps them in `return (...)`.
    if (t.includes('\n')) {
        if (/\breturn\b/.test(t)) {
            return true;
        }
        if (/^\s*(let|var|const|function|if|for|while|switch|try)\b/m.test(t)) {
            return true;
        }
        return false;
    }
    return false;
}
function resolveConditionExecutionKind(inline) {
    return isMultiStatementCondition(inline) ? 'condition-statements' : 'condition-expression';
}
function resolveExpressionExecutionKind(inline) {
    return isMultiStatementCondition(inline) ? 'expression-statements' : 'expression';
}
/**
 * Body placed inside `new Function` (after `'use strict';`), before wrapping for expression-only inline.
 */
function buildScriptFunctionBody(prelude, inline, kind) {
    const p = prelude?.trim();
    const trimmedInline = inline.trim();
    switch (kind) {
        case 'effect':
        case 'condition-statements':
        case 'expression-statements':
            return p ? `${p}\n${inline}` : inline;
        case 'condition-expression':
        case 'expression':
            if (p) {
                return `${p}\nreturn (${trimmedInline});`;
            }
            return trimmedInline;
        default:
            return inline;
    }
}
/** Full source passed to `new Function(..., source)` including strict prefix. */
function buildExecutableFunctionSource(prelude, inline, kind) {
    const body = buildScriptFunctionBody(prelude, inline, kind);
    const p = prelude?.trim();
    if (kind === 'effect' || kind === 'condition-statements' || kind === 'expression-statements') {
        return `'use strict'; ${body}`;
    }
    if (p) {
        return `'use strict'; ${body}`;
    }
    return `'use strict'; return (${body});`;
}
function validateForbiddenPatterns(script) {
    for (const { pattern, name } of FORBIDDEN_PATTERNS) {
        if (pattern.test(script)) {
            throw new Error(`Forbidden pattern in script: ${name}`);
        }
    }
}
/** Validate merged `## Scripts` prelude at adventure load. */
function validatePrelude(prelude) {
    if (prelude.length > exports.ADVENTURE_SCRIPT_MAX_PRELUDE_LENGTH) {
        throw new Error(`Adventure scripts exceed maximum length of ${exports.ADVENTURE_SCRIPT_MAX_PRELUDE_LENGTH} characters`);
    }
    validateForbiddenPatterns(prelude);
    const topLevelFunctions = [];
    let braceDepth = 0;
    for (const rawLine of prelude.split('\n')) {
        const line = rawLine.trim();
        if (!line || line.startsWith('//')) {
            continue;
        }
        if (braceDepth === 0) {
            if (/^(let|var)\s+[a-zA-Z_$]/.test(line)) {
                throw new Error('Adventure scripts: top-level let/var is not allowed; use const or declared Variables');
            }
            if (/^[a-zA-Z_$][\w$]*\s*=/.test(line) && !/^const\s+/.test(line)) {
                throw new Error('Adventure scripts: top-level assignment is not allowed; use const or declared Variables');
            }
            const fnMatch = line.match(/^function\s+([a-zA-Z_$][\w$]*)\b/);
            if (fnMatch) {
                topLevelFunctions.push(fnMatch[1]);
            }
            const constMatch = line.match(/^const\s+([a-zA-Z_$][\w$]*)\b/);
            if (constMatch) {
                const name = constMatch[1];
                if (adventureScriptReservedNames_1.ADVENTURE_SCRIPT_RESERVED_NAMES.has(name)) {
                    throw new Error(`Adventure script declares reserved name: ${name}`);
                }
            }
            if (fnMatch && adventureScriptReservedNames_1.ADVENTURE_SCRIPT_RESERVED_NAMES.has(fnMatch[1])) {
                throw new Error(`Adventure script declares reserved name: ${fnMatch[1]}`);
            }
        }
        const open = (rawLine.match(/{/g) || []).length;
        const close = (rawLine.match(/}/g) || []).length;
        braceDepth += open - close;
        if (braceDepth < 0) {
            braceDepth = 0;
        }
    }
    const seenFunctions = new Set();
    for (const name of topLevelFunctions) {
        if (seenFunctions.has(name)) {
            throw new Error(`Duplicate adventure script function: ${name}`);
        }
        seenFunctions.add(name);
    }
    try {
        new Function(prelude);
    }
    catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        throw new Error(`Adventure script syntax error: ${message}`);
    }
}
/**
 * Validate inline script at execution time. Prelude length is not counted toward the 5000 inline cap.
 */
function validateExecutionScript(prelude, inline, kind, adventureId) {
    if (!inline || inline.trim().length === 0) {
        return;
    }
    const combinedForSecurity = prelude?.trim() ? `${prelude}\n${inline}` : inline;
    validateForbiddenPatterns(combinedForSecurity);
    if (inline.length > exports.INLINE_SCRIPT_MAX_LENGTH) {
        throw formatScriptError('Script exceeds maximum length of 5000 characters', adventureId, !!prelude?.trim());
    }
    const fnSource = buildExecutableFunctionSource(prelude, inline, kind);
    try {
        new Function(fnSource);
    }
    catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        throw formatScriptError(`Script syntax error: ${message}`, adventureId, !!prelude?.trim());
    }
}
function formatScriptError(message, adventureId, hasPrelude) {
    if (hasPrelude && adventureId) {
        return new Error(`[Adventure ${adventureId}] ${message}`);
    }
    return new Error(message);
}
//# sourceMappingURL=adventureScriptValidation.js.map